import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLocation } from "../lib/locationContext";
import { Search, Star, MapPin, ArrowRight, Sparkles, Flame, Gem } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import AdBanner from "../components/AdBanner";

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [trending, setTrending] = useState([]);
  const [hiddenGems, setHiddenGems] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { location, locations } = useLocation();
  const currentLoc = locations.find(l => l.slug === location);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get(`/restaurants/featured?location=${location}`),
      api.get("/influencers"),
      api.get("/reviews?limit=6"),
      api.get(`/discover/trending?location=${location}`),
      api.get(`/discover/hidden-gems?location=${location}`),
    ]).then(([c, r, i, rv, t, g]) => {
      setCategories(c.data);
      setRestaurants(r.data);
      setInfluencers(i.data.slice(0, 4));
      setReviews(rv.data);
      setTrending(t.data);
      setHiddenGems(g.data);
    }).catch(console.error);
  }, [location]);

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.trim()) params.set("q", search.trim());
    navigate(`/restaurants?${params.toString()}`);
  };

  const contextChips = [
    { label: "🔥 Trending", q: "trending" },
    { label: "💎 Hidden Gem", q: "gem" },
    { label: "☕ Cafe Aesthetic", cat: "kopi" },
    { label: "💰 Murah Meriah", q: "murah" },
    { label: "🌙 Buka Malam", q: "malam" },
    { label: "🍱 Tempat Nongkrong", q: "nongkrong" },
  ];

  const goChip = (c) => {
    if (c.cat) navigate(`/restaurants?category=${c.cat}`);
    else if (c.q) navigate(`/restaurants?q=${encodeURIComponent(c.q)}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-secondary border border-border rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] mb-6">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>Discover {currentLoc?.name || "Indonesian"} Cuisines</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
              Semua Kuliner <br />
              <span className="text-primary italic font-serif">{currentLoc?.name || "Indonesia"}</span> <br />
              dalam satu peta rasa.
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">
              Cari restoran berdasarkan kategori, baca review jujur dari influencer kuliner terpercaya,
              cek jam buka real-time, dan langsung navigasi ke lokasinya. Tanpa scroll Google atau Instagram lagi.
            </p>
            <form onSubmit={submitSearch} className="mt-8 flex gap-2 max-w-xl" data-testid="hero-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="hero-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cari bakmi, sushi, kopi, atau nama restoran..."
                  className="pl-12 h-14 rounded-full bg-card border-border text-base"
                />
              </div>
              <Button type="submit" size="lg" className="h-14 px-8 rounded-full bg-primary hover:bg-primary/90" data-testid="hero-search-btn">
                Cari
              </Button>
            </form>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-quick-categories">
              {contextChips.map(c => (
                <Badge
                  key={c.label}
                  onClick={() => goChip(c)}
                  variant="secondary"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors py-1.5 px-3 text-sm"
                  data-testid={`context-chip-${c.label.split(" ")[1] || c.label}`}
                >
                  {c.label}
                </Badge>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative h-[420px] lg:h-[520px]">
            <div className="absolute inset-0 grid grid-cols-2 gap-3">
              <div className="space-y-3">
                <div className="rounded-2xl overflow-hidden h-2/3 border border-border">
                  <img src="https://images.pexels.com/photos/36079468/pexels-photo-36079468.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-1/3 border border-border">
                  <img src="https://images.unsplash.com/photo-1625802269761-0f297f2f7d6c?w=400" alt="" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="space-y-3 mt-8">
                <div className="rounded-2xl overflow-hidden h-1/3 border border-border">
                  <img src="https://images.pexels.com/photos/32938736/pexels-photo-32938736.jpeg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-2/3 border border-border">
                  <img src="https://images.unsplash.com/photo-1653049525170-d062fd2c39e6?w=600" alt="" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOMEPAGE HERO AD BANNER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        <AdBanner placement="homepage_hero" />
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Browse by Category</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black mt-2">Mau makan apa hari ini?</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="categories-grid">
          {categories.map(c => (
            <Link
              key={c.slug}
              to={`/restaurants?category=${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="warm-card p-5 flex flex-col items-center gap-2 text-center hover:border-primary"
            >
              <span className="text-3xl">{c.icon}</span>
              <span className="text-sm font-semibold leading-tight">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED RESTAURANTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
              <Flame className="w-4 h-4 text-primary" /> Hot in Gading Serpong
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black mt-2">Restoran paling dibahas</h2>
          </div>
          <Link to="/restaurants" className="text-sm font-semibold flex items-center gap-1 text-primary hover:underline">
            Lihat semua <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-restaurants">
          {restaurants.map(r => {
            const isFeatured = r.featured_until && new Date(r.featured_until) > new Date();
            return (
            <Link key={r.id} to={`/restaurants/${r.slug}`} className="warm-card group" data-testid={`restaurant-card-${r.slug}`}>
              <div className="aspect-[4/3] overflow-hidden bg-muted relative">
                <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {isFeatured && (
                  <div className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-primary text-white px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Featured
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{r.category?.replace("-", " ")}</Badge>
                  {r.price_range && <span>{r.price_range}</span>}
                </div>
                <h3 className="font-heading font-bold text-base leading-tight line-clamp-1">{r.name}</h3>
                <div className="flex items-center justify-between mt-3 text-sm">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="font-semibold">{r.avg_rating?.toFixed(1) || "—"}</span>
                    <span className="text-xs text-muted-foreground">({r.review_count})</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" /> {currentLoc?.name?.split(" ")[0] || "—"}
                  </span>
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      </section>

      {/* TRENDING */}
      {trending.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
                <Flame className="w-4 h-4 text-primary" /> Trending Minggu Ini
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-black mt-2">Paling sering direview</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="trending-restaurants">
            {trending.map((r, i) => (
              <Link key={r.id} to={`/restaurants/${r.slug}`} className="warm-card group relative" data-testid={`trending-${r.slug}`}>
                <div className="absolute top-2 left-2 z-10 bg-primary text-primary-foreground w-7 h-7 rounded-full flex items-center justify-center font-heading font-black text-sm shadow-lg">
                  #{i + 1}
                </div>
                <div className="aspect-square overflow-hidden bg-muted">
                  <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <h3 className="font-heading font-bold text-sm leading-tight line-clamp-1">{r.name}</h3>
                  <div className="flex items-center gap-1 mt-1 text-xs">
                    <Star className="w-3 h-3 fill-primary text-primary" />
                    <span className="font-semibold">{r.avg_rating?.toFixed(1) || "—"}</span>
                    <span className="text-muted-foreground">({r.review_count})</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* HIDDEN GEMS */}
      {hiddenGems.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
                <Gem className="w-4 h-4 text-primary" /> Hidden Gems
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-black mt-2">Tempat underrated tapi enak</h2>
              <p className="text-muted-foreground text-sm mt-1">Rating tinggi, review masih sedikit — saatnya kamu duluan yang mencoba.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hidden-gems">
            {hiddenGems.slice(0, 3).map(r => (
              <Link key={r.id} to={`/restaurants/${r.slug}`} className="warm-card group" data-testid={`gem-${r.slug}`}>
                <div className="aspect-video overflow-hidden bg-muted relative">
                  <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-primary text-white px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Gem className="w-3 h-3" /> Hidden Gem
                  </div>
                </div>
                <div className="p-4">
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{r.category?.replace("-", " ")}</Badge>
                  <h3 className="font-heading font-bold text-lg mt-2">{r.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{r.description}</p>
                  <div className="flex items-center gap-1 mt-3">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="font-semibold">{r.avg_rating?.toFixed(1)}</span>
                    <span className="text-xs text-muted-foreground">• {r.review_count} review</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* INFLUENCERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Verified Voices</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black mt-2">Influencer kuliner terpercaya</h2>
          </div>
          <Link to="/influencers" className="text-sm font-semibold flex items-center gap-1 text-primary hover:underline">
            Lihat semua <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-influencers">
          {influencers.map(i => (
            <Link key={i.user_id} to={`/influencers/${i.user_id}`} className="warm-card p-5 text-center" data-testid={`influencer-card-${i.user_id}`}>
              <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-primary/20 mb-4">
                <img src={i.picture} alt={i.name} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-heading font-bold">{i.name}</h3>
              <p className="text-xs text-primary mt-1">{i.instagram_handle}</p>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{i.bio}</p>
              <Badge variant="secondary" className="mt-3 text-[10px]">{i.review_count} reviews</Badge>
            </Link>
          ))}
        </div>
      </section>

      {/* RECENT REVIEWS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Fresh Reviews</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black mt-2">Review terbaru</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="recent-reviews">
          {reviews.map(r => (
            <Link
              key={r.id}
              to={`/restaurants/${r.restaurant?.slug}`}
              className="warm-card overflow-hidden group"
              data-testid={`review-card-${r.id}`}
            >
              <div className="aspect-video overflow-hidden bg-muted relative">
                <img src={r.cover_image || r.restaurant?.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> {r.overall.toFixed(1)}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <img src={r.influencer?.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                  <span className="text-xs font-semibold">{r.influencer?.name}</span>
                  <span className="text-xs text-muted-foreground">→ {r.restaurant?.name}</span>
                </div>
                <h3 className="font-heading font-bold leading-tight line-clamp-2">{r.title}</h3>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div><div className="text-muted-foreground">Rasa</div><div className="font-bold">{r.rasa}</div></div>
                  <div><div className="text-muted-foreground">Harga</div><div className="font-bold">{r.harga}</div></div>
                  <div><div className="text-muted-foreground">Ambience</div><div className="font-bold">{r.ambience}</div></div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="rounded-3xl terracotta-gradient text-white p-8 lg:p-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl lg:text-5xl font-black leading-tight">
              Punya restoran di {currentLoc?.city || "Indonesia"}?
            </h2>
            <p className="mt-4 text-white/90 text-lg max-w-md">
              Daftarkan gratis. Kelola menu, jam buka, dan posting pengumuman libur langsung ke pelanggan.
            </p>
            <Link to="/dashboard">
              <Button size="lg" variant="secondary" className="mt-6 rounded-full" data-testid="cta-register-restaurant">
                Daftar Sekarang <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="hidden md:block">
            <img src="https://images.unsplash.com/photo-1774763343132-da7461ecd1ea?w=600" className="rounded-2xl shadow-2xl" alt="" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
